import React, {useState} from 'react';
import useAsyncEffect from 'use-async-effect';
import {connect} from 'react-redux';
import {match} from 'react-router-dom';
import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {toast} from 'react-toastify';

import {AppState} from '../Store';
import {Author, AuthorService} from '../Services/AuthorService';
import {loadAuthorsCreator} from '../Store/Admin/Actions';

import {Link} from 'react-router-dom';
import ImageUpload from './ImageUpload';
import AuthorProfile from '../Components/AuthorProfile';

type TParams = {
  id?: string
};

interface AuthorsProps {
  match: match<TParams>;

  authorService: AuthorService;
  authors: Author[];

  loadAuthors: () => Promise<void>;
}

function Authors({match, authorService, authors, loadAuthors}: AuthorsProps) {
  const [author, setAuthor] = useState<Author | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useAsyncEffect(async isMounted => {
    const {params: {id}} = match;
    if (author === undefined && id !== undefined) {
      if (id === 'new') {
        setAuthor({
          id: -1,
          name: '',
          position: '',
          image: -1,
        });
      }
      try {
        const res = await authorService.byId(Number(id));
        if (res.parsedBody !== undefined && isMounted()) {
          setAuthor(res.parsedBody);
        }
      } catch (e) {
        console.log(e);
      }
    }
  },[match]);

  const nameChanged = (e: React.FormEvent<HTMLInputElement>) => {
    if (author === undefined) {
      return;
    }
    setAuthor({
      ...author,
      name: e.currentTarget.value,
    });
  };

  const positionChanged = (e: React.FormEvent<HTMLInputElement>) => {
    if (author === undefined) {
      return;
    }
    setAuthor({
      ...author,
      position: e.currentTarget.value,
    });
  };

  const imageChanged = (image: number) => {
    if (author === undefined) {
      return;
    }

    setAuthor({
      ...author,
      image,
    });
  };

  const save = async () => {
    if (author === undefined) {
      toast.info("Honestly, I'm kinda impressed you managed to do that");
      return;
    }

    setSaving(true);

    if (author.id === -1) {
      try {
        const res = await authorService.create(author);
        setAuthor(res.parsedBody);
        toast.success("Author created!");
        loadAuthors();
      } catch {
        toast.error("Failed to save author");
      }
    } else {
      try {
        const res = await authorService.update(author);
        setAuthor(res.parsedBody);
        toast.success("Author saved!");
        loadAuthors();
      } catch {
        toast.error("Failed to save author");
      }
    }

    setSaving(false);
  };

  const back = () => {
    setAuthor(undefined);
  };

  if (author === undefined) {
    return (
      <div className="post-content-area mb-100">
        <div className="world-category-area">
          <ul className="nav nav-tabs" role="tablist">
            <li className="title"><Link to="/admin/authors/new">New Author</Link></li>
          </ul>
          <div className="tab-content row">
            {authors.map(a => {
              return <div key={a.id} className="col-3"><AuthorProfile author={a} /></div>;
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="post-content-area mb-100">
        <div className="world-category-area">
          <ul className="nav nav-tabs" role="tablist">
            <li className="title"><Link to="/admin/authors" onClick={back}>Back</Link></li>
          </ul>
        </div>
      </div>
      <div className="article-edit">
        <fieldset disabled={saving}>
          <div className="row">
            <label className="col-sm-2">ID</label>
            <div className="col-sm-10">
              {author.id}
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <div className="form-group row">
                <label className="col-sm-2">Name</label>
                <div className="col-sm-10">
                  <input className="form-control" type="text" onChange={nameChanged} value={author.name} />
                </div>
              </div>
              <div className="form-group row">
                <label className="col-sm-2">Position</label>
                <div className="col-sm-10">
                  <input className="form-control" type="text" onChange={positionChanged} value={author.position} />
                </div>
              </div>
            </div>
            <div className="col-sm-6">
              <ImageUpload id={author.image} onUpload={imageChanged} />
            </div>
          </div>
          <button className="btn btn-primary" type="submit" onClick={save}>Save</button>
        </fieldset>
      </div>
    </div>
  );
}

const mapStateToProps = (state: AppState) => {
  return {
    authorService: state.auth.authorService,
    authors: state.admin.authors.list,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    loadAuthors: () => dispatch(loadAuthorsCreator()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Authors);
